import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Cookie, Shield, FileText, X } from "lucide-react";
import { updateAnalyticsConsent } from "../lib/analytics";

const CONSENT_KEY = "blackarrow_cookie_consent_v1";

const defaultPrefs = { necessary: true, analytics: false, marketing: false };

const loadConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.warn("CookieConsent: unable to read consent from localStorage", err);
    }
    return null;
  }
};

const saveConsent = (prefs) => {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ ...prefs, timestamp: new Date().toISOString() })
    );
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.warn("CookieConsent: unable to persist consent to localStorage", err);
    }
  }
};

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    const consent = loadConsent();
    if (!consent) {
      setShowBanner(true);
    } else {
      const restored = { necessary: true, analytics: !!consent.analytics, marketing: !!consent.marketing };
      setPrefs(restored);
      // Restore analytics consent on page load
      updateAnalyticsConsent(restored);
    }

    const openPrivacy = () => setShowPrivacy(true);
    const openCookiePolicy = () => setShowCookiePolicy(true);
    const openPrefs = () => {
      setShowPrefs(true);
      setShowBanner(false);
    };

    window.addEventListener("open-privacy", openPrivacy);
    window.addEventListener("open-cookie-policy", openCookiePolicy);
    window.addEventListener("open-cookie-preferences", openPrefs);

    return () => {
      window.removeEventListener("open-privacy", openPrivacy);
      window.removeEventListener("open-cookie-policy", openCookiePolicy);
      window.removeEventListener("open-cookie-preferences", openPrefs);
    };
  }, []);

  const acceptAll = () => {
    const next = { necessary: true, analytics: true, marketing: true };
    setPrefs(next);
    saveConsent(next);
    updateAnalyticsConsent(next);
    setShowBanner(false);
    setShowPrefs(false);
  };

  const rejectAll = () => {
    const next = { necessary: true, analytics: false, marketing: false };
    setPrefs(next);
    saveConsent(next);
    updateAnalyticsConsent(next);
    setShowBanner(false);
    setShowPrefs(false);
  };

  const saveCustom = () => {
    saveConsent(prefs);
    updateAnalyticsConsent(prefs);
    setShowBanner(false);
    setShowPrefs(false);
  };

  return (
    <>
      {showBanner && (
        <div
          data-testid="cookie-banner"
          className="fixed bottom-0 left-0 right-0 z-[100] bg-black/95 backdrop-blur-md border-t border-gold/40 shadow-[0_-8px_32px_rgba(212,175,55,0.15)]"
        >
          <div className="max-w-7xl mx-auto px-4 py-5 md:py-6 flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-6">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="h-6 w-6 text-gold shrink-0 mt-1" />
              <div className="text-sm text-white/90 leading-relaxed">
                <p className="font-serif text-gold text-base mb-1">La tua privacy è importante</p>
                <p>
                  Utilizziamo cookie tecnici necessari al funzionamento del sito e, previo tuo consenso, cookie di analisi e marketing per migliorare la tua esperienza. Puoi accettare, rifiutare o personalizzare le tue preferenze in qualsiasi momento. Per maggiori dettagli leggi la nostra{" "}
                  <button
                    type="button"
                    onClick={() => setShowCookiePolicy(true)}
                    className="text-gold underline hover:text-gold-light"
                    data-testid="banner-link-cookie-policy"
                  >
                    Cookie Policy
                  </button>{" "}
                  e la{" "}
                  <button
                    type="button"
                    onClick={() => setShowPrivacy(true)}
                    className="text-gold underline hover:text-gold-light"
                    data-testid="banner-link-privacy"
                  >
                    Privacy Policy
                  </button>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto shrink-0">
              <Button
                variant="outline"
                onClick={rejectAll}
                className="border-gold/50 text-gold hover:bg-gold/10 hover:text-gold"
                data-testid="cookie-reject-btn"
              >
                Rifiuta
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowPrefs(true); setShowBanner(false); }}
                className="border-gold/50 text-gold hover:bg-gold/10 hover:text-gold"
                data-testid="cookie-customize-btn"
              >
                Personalizza
              </Button>
              <Button
                onClick={acceptAll}
                className="bg-gold text-black hover:bg-gold-light font-semibold"
                data-testid="cookie-accept-btn"
              >
                Accetta tutti
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Dialog */}
      <Dialog open={showPrefs} onOpenChange={setShowPrefs}>
        <DialogContent className="bg-black border-gold/40 text-white max-w-lg" data-testid="cookie-prefs-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-gold flex items-center gap-2">
              <Cookie className="h-5 w-5" /> Preferenze Cookie
            </DialogTitle>
            <DialogDescription className="text-white/70">
              Gestisci i consensi per le categorie di cookie utilizzati da questo sito.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 p-4 rounded-md border border-gold/20 bg-white/5">
              <div className="flex-1">
                <Label className="text-gold font-semibold">Cookie Necessari</Label>
                <p className="text-xs text-white/60 mt-1">
                  Indispensabili per il funzionamento del sito (sessione, sicurezza, preferenze). Non possono essere disattivati.
                </p>
              </div>
              <Switch checked disabled data-testid="prefs-necessary" />
            </div>

            <div className="flex items-start justify-between gap-4 p-4 rounded-md border border-gold/20 bg-white/5">
              <div className="flex-1">
                <Label htmlFor="analytics" className="text-gold font-semibold">Cookie di Analisi</Label>
                <p className="text-xs text-white/60 mt-1">
                  Ci aiutano a capire come i visitatori utilizzano il sito, in forma anonima o aggregata.
                </p>
              </div>
              <Switch
                id="analytics"
                checked={prefs.analytics}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                data-testid="prefs-analytics"
              />
            </div>

            <div className="flex items-start justify-between gap-4 p-4 rounded-md border border-gold/20 bg-white/5">
              <div className="flex-1">
                <Label htmlFor="marketing" className="text-gold font-semibold">Cookie di Marketing</Label>
                <p className="text-xs text-white/60 mt-1">
                  Utilizzati per mostrare contenuti personalizzati e misurare l'efficacia delle campagne.
                </p>
              </div>
              <Switch
                id="marketing"
                checked={prefs.marketing}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                data-testid="prefs-marketing"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={rejectAll}
              className="border-gold/50 text-gold hover:bg-gold/10 hover:text-gold"
              data-testid="prefs-reject-all"
            >
              Rifiuta tutti
            </Button>
            <Button
              variant="outline"
              onClick={saveCustom}
              className="border-gold/50 text-gold hover:bg-gold/10 hover:text-gold"
              data-testid="prefs-save"
            >
              Salva preferenze
            </Button>
            <Button
              onClick={acceptAll}
              className="bg-gold text-black hover:bg-gold-light font-semibold"
              data-testid="prefs-accept-all"
            >
              Accetta tutti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Dialog */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent className="bg-black border-gold/40 text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="privacy-policy-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-gold flex items-center gap-2 text-2xl">
              <Shield className="h-5 w-5" /> Privacy Policy
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Informativa ai sensi dell'art. 13 del Regolamento UE 2016/679 (GDPR)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm text-white/85 leading-relaxed">
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">1. Titolare del trattamento</h3>
              <p>
                Il Titolare del trattamento dei dati è <strong>Black Arrow NCC</strong>, con sede in Viale Libertà 55, Paduli (BN), Italia. Contatti: <a href="mailto:blackarrow25@libero.it" className="text-gold underline">blackarrow25@libero.it</a> — +39 328 379 6150.
              </p>
            </section>
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">2. Dati raccolti</h3>
              <p>
                Raccogliamo i dati personali forniti volontariamente tramite il modulo di prenotazione (nome, telefono, email, indirizzi di pick-up e destinazione, data e ora del servizio) e, ove applicabile, dati di navigazione tecnici (indirizzo IP, user-agent, log di accesso).
              </p>
            </section>
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">3. Finalità e base giuridica</h3>
              <p>
                I dati sono trattati per: (a) gestione della prenotazione e fornitura del servizio NCC richiesto (art. 6.1.b GDPR — esecuzione del contratto); (b) adempimenti fiscali e di legge (art. 6.1.c); (c) previo consenso, invio di comunicazioni promozionali (art. 6.1.a).
              </p>
            </section>
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">4. Conservazione</h3>
              <p>
                I dati di prenotazione sono conservati per il tempo strettamente necessario all'erogazione del servizio e agli obblighi di legge (fino a 10 anni per la documentazione fiscale). I dati di marketing vengono conservati fino alla revoca del consenso.
              </p>
            </section>
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">5. Destinatari</h3>
              <p>
                I dati possono essere comunicati a: autisti incaricati, fornitori di servizi IT, consulenti fiscali, autorità pubbliche nei casi previsti dalla legge. Non vengono diffusi né trasferiti fuori dallo Spazio Economico Europeo.
              </p>
            </section>
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">6. Diritti dell'interessato</h3>
              <p>
                Hai diritto di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al trattamento (artt. 15-22 GDPR), nonché di revocare il consenso in qualsiasi momento e di proporre reclamo al Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-gold underline">www.garanteprivacy.it</a>).
              </p>
            </section>
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">7. Contatti per esercitare i diritti</h3>
              <p>
                Per esercitare i tuoi diritti scrivi a <a href="mailto:blackarrow25@libero.it" className="text-gold underline">blackarrow25@libero.it</a>.
              </p>
            </section>
            <p className="text-xs text-white/50 pt-2 border-t border-gold/20">Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT")}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cookie Policy Dialog */}
      <Dialog open={showCookiePolicy} onOpenChange={setShowCookiePolicy}>
        <DialogContent className="bg-black border-gold/40 text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cookie-policy-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-gold flex items-center gap-2 text-2xl">
              <FileText className="h-5 w-5" /> Cookie Policy
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Informativa sull'uso dei cookie — Provvedimento Garante Privacy 10/06/2021
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm text-white/85 leading-relaxed">
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">Cosa sono i cookie</h3>
              <p>
                I cookie sono piccoli file di testo che i siti visitati inviano al terminale dell'utente, dove vengono memorizzati per essere poi ritrasmessi agli stessi siti alla visita successiva.
              </p>
            </section>
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">Tipologie di cookie utilizzati</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong className="text-gold">Necessari (tecnici)</strong>: indispensabili al funzionamento del sito. Non richiedono consenso.
                </li>
                <li>
                  <strong className="text-gold">Analitici</strong>: raccolgono informazioni in forma aggregata sul numero degli utenti e su come visitano il sito. Attivati solo previo consenso.
                </li>
                <li>
                  <strong className="text-gold">Marketing</strong>: utilizzati per profilazione e pubblicità personalizzata. Attivati solo previo consenso.
                </li>
              </ul>
            </section>
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">Gestione del consenso</h3>
              <p>
                Puoi modificare in qualsiasi momento le tue preferenze cliccando su{" "}
                <button
                  type="button"
                  onClick={() => { setShowCookiePolicy(false); setShowPrefs(true); }}
                  className="text-gold underline hover:text-gold-light"
                  data-testid="policy-open-prefs"
                >
                  Gestisci preferenze cookie
                </button>
                . Puoi inoltre disabilitare i cookie direttamente dal browser.
              </p>
            </section>
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">Durata</h3>
              <p>
                I cookie possono essere di sessione (cancellati alla chiusura del browser) o persistenti (con durata massima di 12 mesi).
              </p>
            </section>
            <section>
              <h3 className="text-gold font-serif text-lg mb-1">Titolare</h3>
              <p>
                Black Arrow NCC — Viale Libertà 55, Paduli (BN) — <a href="mailto:blackarrow25@libero.it" className="text-gold underline">blackarrow25@libero.it</a>
              </p>
            </section>
            <p className="text-xs text-white/50 pt-2 border-t border-gold/20">Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT")}</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
